import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { Plus, ChevronLeft, ChevronRight, Check } from 'lucide-react'
import { useReactFlow } from '@xyflow/react'
import type { NodeCategory } from '@bandos/shared'
import { SparkleLoader } from '../../../common/SparkleLoader'
import { getTargetInfo } from '../../Menus/CategoryOptionsList.utils'
import type { AISuggestion } from '../../types/aiTypes'
import {
  PickerContainer,
  PickerHeader,
  HeaderIcon,
  HeaderTitle,
  PickerCard,
  CardTopRow,
  CounterGroup,
  Counter,
  AddedBadge,
  NavArrows,
  NavArrowButton,
  CardContent,
  CardActions,
  AddButton,
  SkipButton,
  ProgressTrack,
  ProgressDot,
  LoadingBody,
  FallbackTitle,
  FallbackDescription,
} from './SuggestionPickerStyles'

// ── Suggestion content renderer (reused across all node types) ──

function PickerContent({ suggestion }: { suggestion: AISuggestion }): React.ReactElement {
  return (
    <div>
      {suggestion.title && <FallbackTitle>{suggestion.title}</FallbackTitle>}
      {suggestion.description && (
        <FallbackDescription>
          {suggestion.description.split('\n').map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </FallbackDescription>
      )}
    </div>
  )
}

// ── Main picker component ──

interface SuggestionPickerProps {
  nodeId: string
  suggestions: AISuggestion[]
  isGenerating: boolean
  onAcceptSuggestion: (suggestionId: string) => void
}

export const SuggestionPicker = React.memo(
  ({
    nodeId,
    suggestions,
    isGenerating,
    onAcceptSuggestion,
  }: SuggestionPickerProps): React.ReactElement | null => {
    const { getNode } = useReactFlow()
    const [currentIndex, setCurrentIndex] = useState(0)
    const [addedIds, setAddedIds] = useState<Set<string>>(new Set())
    const cardRef = useRef<HTMLDivElement>(null)

    const targetLabel = useMemo(() => {
      const node = getNode(nodeId)
      const category = node?.data?.category as NodeCategory | undefined
      const info = getTargetInfo(category)
      return info?.targetLabel ?? 'option'
    }, [getNode, nodeId])

    const article = /^[aeiou]/i.test(targetLabel) ? 'an' : 'a'
    const total = suggestions.length
    // Clamp so the index is always valid even if the suggestions array shrinks
    const safeIndex = total > 0 ? Math.min(currentIndex, total - 1) : 0
    const currentSuggestion = suggestions[safeIndex]
    const isCurrentAdded = currentSuggestion ? addedIds.has(currentSuggestion.id) : false

    // Keep currentIndex in sync when clamping changes it
    useEffect(() => {
      if (safeIndex !== currentIndex) {
        setCurrentIndex(safeIndex)
      }
    }, [safeIndex, currentIndex])

    // Scroll the card bottom into view when navigating to a longer item
    useEffect(() => {
      if (!cardRef.current) return
      // Small delay so the DOM measures the new content height first
      requestAnimationFrame(() => {
        cardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    }, [currentIndex])

    const goToPrev = useCallback((): void => {
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev))
    }, [])

    const goToNext = useCallback((): void => {
      setCurrentIndex((prev) => (prev < total - 1 ? prev + 1 : prev))
    }, [total])

    const advanceToNext = useCallback((): void => {
      setCurrentIndex((prev) => (prev < total - 1 ? prev + 1 : prev))
    }, [total])

    const handleAdd = useCallback(
      (e: React.MouseEvent): void => {
        e.stopPropagation()
        if (!currentSuggestion || isCurrentAdded) return
        setAddedIds((prev) => new Set(prev).add(currentSuggestion.id))
        onAcceptSuggestion(currentSuggestion.id)
        advanceToNext()
      },
      [currentSuggestion, isCurrentAdded, onAcceptSuggestion, advanceToNext]
    )

    const handleSkip = useCallback(
      (e: React.MouseEvent): void => {
        e.stopPropagation()
        advanceToNext()
      },
      [advanceToNext]
    )

    if (!isGenerating && total === 0) return null

    const isAtStart = safeIndex === 0
    const isAtEnd = safeIndex === total - 1

    return (
      <PickerContainer>
        <PickerHeader>
          <HeaderIcon>
            <Plus size={14} strokeWidth={2.5} />
          </HeaderIcon>
          <HeaderTitle>
            Pick {article} {targetLabel.toLowerCase()} to add
          </HeaderTitle>
        </PickerHeader>

        <PickerCard ref={cardRef}>
          {isGenerating && (
            <LoadingBody>
              <SparkleLoader isAnimating size={20} />
            </LoadingBody>
          )}

          {!isGenerating && currentSuggestion && (
            <>
              <CardTopRow>
                <CounterGroup>
                  <Counter>
                    {safeIndex + 1} of {total}
                  </Counter>
                  {isCurrentAdded && (
                    <>
                      <Counter>&middot;</Counter>
                      <AddedBadge>Added</AddedBadge>
                    </>
                  )}
                </CounterGroup>
                <NavArrows>
                  <NavArrowButton disabled={isAtStart} onClick={goToPrev}>
                    <ChevronLeft size={14} />
                  </NavArrowButton>
                  <NavArrowButton disabled={isAtEnd} onClick={goToNext}>
                    <ChevronRight size={14} />
                  </NavArrowButton>
                </NavArrows>
              </CardTopRow>

              <CardContent>
                <PickerContent suggestion={currentSuggestion} />
              </CardContent>

              <CardActions>
                <AddButton $added={isCurrentAdded} onClick={handleAdd}>
                  {isCurrentAdded ? (
                    <>
                      <Check size={13} strokeWidth={2.5} />
                      Added
                    </>
                  ) : (
                    <>
                      <Plus size={13} strokeWidth={2.5} />
                      Add{targetLabel !== 'option' ? ` ${targetLabel}` : ''}
                    </>
                  )}
                </AddButton>
                {!isCurrentAdded && <SkipButton onClick={handleSkip}>Skip</SkipButton>}
              </CardActions>

              <ProgressTrack>
                {suggestions.map((s, i) => {
                  const dotState =
                    i === safeIndex ? 'active' : addedIds.has(s.id) ? 'added' : 'pending'
                  return <ProgressDot key={s.id} $state={dotState} />
                })}
              </ProgressTrack>
            </>
          )}
        </PickerCard>
      </PickerContainer>
    )
  }
)

SuggestionPicker.displayName = 'SuggestionPicker'
